import React, { useCallback, useState } from 'react';
import styles from './styles.module.scss';
import { Interview } from '../../../entities/Interview';
import { InterviewMarker } from '../../../entities/InterviewMarker';
import { NoteBlock } from '../NoteBlock';
import { NoteService } from '../../../services/NoteService';
import { InterviewService } from '../../../services/InterviewService';
import { NoteInput } from '../../../components/NoteInput';
import { Modal } from '../../../components/Modal';
import { InterviewMarkerService } from '../../../services/InterviewMarkerService';
import { ThreadData } from '../../../entities/ThreadData';
import { InterviewAutomaticQuestion } from '../../../entities/InterviewAutomaticQuestion';
import { MenuType } from '../MenuType';
import { AutomaticNotesBlock } from './AutomaticNotesBlock';
import { Button } from 'reactstrap';

export interface PropTypes {
  playTimestamp?: number;
  interview: Interview;
  threadData: ThreadData;
  showIcon: boolean;
  menuType: MenuType;
  onSelectTimestamp: (timestamp: number) => void;
  onUpdateInterviewMarkers?: (InterviewMarkers: InterviewMarker[]) => void;
  onUpdateInterviewAutomaticQuestions?: (
    interviewMarkers: InterviewAutomaticQuestion[],
  ) => void;
}

const SubBlock = ({ children }: { children: JSX.Element }) => {
  return (
    <div className='col-12'>
      <div className='row'>
        <div className='col-auto'>
          <div className={styles.linkLine} />
        </div>
        <div className='col mt-2 ps-0'>{children}</div>
      </div>
    </div>
  );
};

function hasBody(threadData: ThreadData) {
  if (threadData.kind === 'automatic_scorecard_attribute') {
    return true;
  }

  return (
    threadData.userId !== undefined &&
    threadData.userId === threadData.note?.user?.id
  );
}

function extractBody(threadData: ThreadData) {
  if (threadData.redacted) {
    return `This content was redacted and it's not available for your user permissions.`;
  }

  if (threadData.question != null) {
    return threadData.question;
  }

  if (threadData.scorecard_attribute != null) {
    return threadData.scorecard_attribute?.name;
  }

  if (hasBody(threadData)) return threadData.note.text;

  return undefined;
}

function extractReplies(threadData: ThreadData) {
  return threadData.replies || [];
}

function extractAutomaticNotes(threadData: ThreadData) {
  return threadData.automatic_notes || [];
}

export const NotesThread = React.memo(function NotesThread(props: PropTypes) {
  const noteActive = props.playTimestamp !== undefined;
  const [noteText, setNoteText] = useState<string>('');
  const [replyActive, setReplyActive] = useState<boolean>(false);
  const body = extractBody(props.threadData);
  const replies = extractReplies(props.threadData);
  const automaticNotes = extractAutomaticNotes(props.threadData);

  const [deleteNoteId, setDeleteNoteId] = useState<number>();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  async function handlePostReply(text: string) {
    if (props.threadData.kind === 'automatic_scorecard_attribute') {
      await NoteService.createAutomaticAttributeReply(
        props.threadData.item_id,
        text,
      );
    } else if (props.threadData.kind === 'automatic_question') {
      await NoteService.createQuestionReply(props.threadData.item_id, text);
    } else {
      await NoteService.createNoteReply(props.threadData.item_id, text);
    }

    const interview = await InterviewService.get(props.interview.id);

    if (props.threadData.kind == 'automatic_question') {
      props.onUpdateInterviewAutomaticQuestions?.(
        interview.interview_automatic_questions,
      );
    } else {
      props.onUpdateInterviewMarkers?.(interview.interview_markers);
    }

    setReplyActive(false);
    setNoteText('');
  }

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handlePostReply(noteText);
    },
    [noteText],
  );

  function handleClickDelete(noteId: number | null) {
    setDeleteNoteId(noteId);
    setDeleteModalOpen(true);
  }

  async function handleDeleteInterviewMarkerOrNote() {
    let updatedNoteReplies;
    if (deleteNoteId === null) {
      await InterviewMarkerService.destroy(props.threadData.item_id);

      updatedNoteReplies = props.interview.interview_markers.filter(
        (iv) => iv.id !== props.threadData.id,
      );
    } else {
      await NoteService.destroy(deleteNoteId);

      if (props.threadData.kind === 'automatic_question') {
        updatedNoteReplies = JSON.parse(
          JSON.stringify(props.interview.interview_automatic_questions),
        );
      } else {
        updatedNoteReplies = JSON.parse(
          JSON.stringify(props.interview.interview_markers),
        );
      }
      const thisInterviewNote = updatedNoteReplies.find(
        (im) => im.id === props.threadData.item_id,
      );
      thisInterviewNote.replies = thisInterviewNote.replies.filter(
        (n) => n.id !== deleteNoteId,
      );
    }
    setDeleteModalOpen(false);

    if (props.threadData.kind === 'automatic_question') {
      props.onUpdateInterviewAutomaticQuestions(updatedNoteReplies);
    } else {
      props.onUpdateInterviewMarkers(updatedNoteReplies);
    }
  }

  const kind = props.threadData.redacted
    ? 'interview_secret'
    : props.threadData.kind;

  return (
    <div
      className={`p-2 mb-3 ${styles.notesThreadWrapper} ${
        noteActive ? styles.notesThreadWrapperActive : ''
      }`}
      data-testid={`NotesThread-${props.threadData.id}`}
    >
      <div className='col-12' data-testid={`NoteBlock-${props.threadData.id}`}>
        <NoteBlock
          icon={props.showIcon ? props.threadData.icon_classes : undefined}
          offsetMs={props.threadData.offset_ms}
          totalTime={props.interview.media.duration}
          displayName={props.threadData.display_name}
          body={body}
          scoreBody={props.threadData.score}
          contextBody={props.threadData.context_question}
          kind={kind}
          noteId={null}
          deleteAccess={props.threadData.note?.delete_access}
          menuType={props.menuType}
          onSelectTimestamp={props.onSelectTimestamp}
          onClickReply={() => setReplyActive(true)}
          onClickDelete={handleClickDelete}
        />
      </div>
      <AutomaticNotesBlock
        typeName='Response'
        automaticNotes={automaticNotes}
      />
      {replies.map((note) => (
        <SubBlock key={note.id}>
          <div data-testid={`NoteBlock-reply-${note.id}`}>
            <NoteBlock
              kind='note'
              totalTime={props.interview.media.duration}
              displayName={note.user?.name}
              body={note.text}
              noteId={note.id}
              deleteAccess={note.delete_access}
              menuType={props.menuType}
              onSelectTimestamp={props.onSelectTimestamp}
              onClickDelete={handleClickDelete}
            />
          </div>
        </SubBlock>
      ))}
      {replyActive && (
        <SubBlock>
          <form onSubmit={onSubmit}>
            <div className={styles.replyBoxWrapper}>
              <div className='row'>
                <div className='col-12'>
                  <NoteInput
                    className='fs-5'
                    autofocus={true}
                    placeholder='Type your reply...'
                    value={noteText}
                    onChange={(v) => setNoteText(v)}
                  />
                </div>
                <div className='col-12 text-end mt-2'>
                  <Button
                    onClick={() => setReplyActive(false)}
                    color='danger'
                    size='sm'
                  >
                    Cancel
                  </Button>
                  <Button
                    className='ms-3'
                    color='primary'
                    type='submit'
                    size='sm'
                  >
                    Reply
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </SubBlock>
      )}
      <Modal
        open={deleteModalOpen}
        title='Delete Note'
        body='This message will be permanently deleted'
        cancelButtonText='Cancel'
        confirmButtonText='Delete'
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={() => handleDeleteInterviewMarkerOrNote()}
      />
    </div>
  );
});
